import { useEffect, useState, Suspense, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Canvas, useThree, useFrame } from '@react-three/fiber'
import { CameraControls, Stage, useProgress, PerspectiveCamera } from '@react-three/drei'
import { Vector3 } from 'three'
import { getExperience } from '../api/experience'
import { cacheExperienceMedia, cachePointOfInterests } from '../utils/mediaCache'
import { getPointOfInterestsByModelAndModelId, viewPointOfInterest } from '../api/pointOfInterest'
import { LoadingScreen } from '../components/Loading'
import { PointOfInterestLight, type PointOfInterest } from '../components/PointOfInterest'
import { getAssetUrl } from '../utils'
import { createSession, endSession } from '../api/session'
import EducatePanel from '../components/panels/EducatePanel'
import HomePanel from '../components/panels/HomePanel'
import type ExperienceData from '../types/experience'
import type QuestionData from '../types/question'
import type SessionData from '../types/session'
import RightDrawer from '../components/RightDrawer'
import Model from '../components/Model'
import QuizPanel from '../components/panels/QuizPanel'
import { DottedBg } from '../components/DottedBg'
import { PopupModal } from '../components/PopupModal'
import { audio, type SoundKey, type SrcConfig } from '../utils/audio'
import type Media from '../types/media'

// Simplified camera animator using CameraControls built-in methods
const CameraAnimator = ({
  targetPosition,
  targetLookAt,
  targetRotation,
  isAnimating,
  onAnimationComplete,
  controlsRef,
  duration = 2.5,
}: {
  targetPosition: Vector3
  targetLookAt: Vector3
  targetRotation?: {
    azimuthRotation?: number
    polarRotation?: number
  }
  isAnimating: boolean
  onAnimationComplete: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  controlsRef: React.RefObject<any>
  duration?: number
}) => {
  const animationTriggered = useRef(false)

  useEffect(() => {
    if (!isAnimating) {
      animationTriggered.current = false
      return
    }

    if (animationTriggered.current) return

    // Check if controls are available
    if (!controlsRef.current) {
      return
    }

    // Set the transition speed based on duration
    controlsRef.current.smoothTime = duration * 0.2 // Adjust smoothTime for desired speed

    // Trigger the animation once
    animationTriggered.current = true

    if (
      targetRotation &&
      targetRotation.azimuthRotation !== null &&
      targetRotation.polarRotation !== null &&
      targetRotation.azimuthRotation !== undefined &&
      targetRotation.polarRotation !== undefined
    ) {
      controlsRef.current
        .setLookAt(
          targetPosition.x,
          targetPosition.y,
          targetPosition.z,
          targetLookAt.x,
          targetLookAt.y,
          targetLookAt.z,
          true
        )
        .then(() => {
          animationTriggered.current = false
          onAnimationComplete()
        })
        .catch((error: Error) => {
          console.error('CameraControls animation error:', error)
          animationTriggered.current = false
          onAnimationComplete()
        })
      controlsRef.current
        .rotateTo(
          targetRotation.azimuthRotation, // azimuth (yaw)
          targetRotation.polarRotation, // polar (pitch)
          true // enableTransition
        )
        .then(() => {})
    } else {
      // Use setLookAt for position and look-at target
      controlsRef.current
        .setLookAt(
          targetPosition.x,
          targetPosition.y,
          targetPosition.z,
          targetLookAt.x,
          targetLookAt.y,
          targetLookAt.z,
          true // enableTransition
        )
        .then(() => {
          animationTriggered.current = false
          onAnimationComplete()
        })
        .catch((error: Error) => {
          console.error('CameraControls animation error:', error)
          animationTriggered.current = false
          onAnimationComplete()
        })
    }
  }, [
    isAnimating,
    targetPosition,
    targetLookAt,
    targetRotation,
    controlsRef,
    duration,
    onAnimationComplete,
  ])

  return null
}

// Camera debug component to show current position and target
const CameraDebug = ({
  onCameraUpdate,
  controlsRef,
}: {
  onCameraUpdate: (info: {
    position: { x: number; y: number; z: number }
    target: { x: number; y: number; z: number }
    distance: number
    zoom: number
    azimuth: number
    polar: number
  }) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  controlsRef: React.RefObject<any>
}) => {
  const { camera } = useThree()

  useFrame(() => {
    if (camera) {
      // Get camera position
      const position = {
        x: Number(camera.position.x.toFixed(2)),
        y: Number(camera.position.y.toFixed(2)),
        z: Number(camera.position.z.toFixed(2)),
      }

      // Get target from camera direction (CameraControls doesn't expose target directly)
      const direction = new Vector3()
      camera.getWorldDirection(direction)
      const lookAtPoint = camera.position.clone().add(direction.multiplyScalar(10))
      const target = {
        x: Number(lookAtPoint.x.toFixed(2)),
        y: Number(lookAtPoint.y.toFixed(2)),
        z: Number(lookAtPoint.z.toFixed(2)),
      }

      // Calculate distance from camera to target
      const distance = Math.sqrt(
        Math.pow(camera.position.x - target.x, 2) +
          Math.pow(camera.position.y - target.y, 2) +
          Math.pow(camera.position.z - target.z, 2)
      )

      const azimuth = controlsRef.current?.azimuthAngle
      const polar = controlsRef.current?.polarAngle

      // Get actual camera zoom (for orthographic cameras) or use 1 for perspective
      const zoom =
        camera.type === 'OrthographicCamera'
          ? Number((camera as { zoom: number }).zoom.toFixed(2))
          : 1

      const info = {
        position,
        target,
        distance: Number(distance.toFixed(2)),
        zoom,
        azimuth,
        polar,
      }
      onCameraUpdate(info)
    }
  })

  return null // This component doesn't render anything in the 3D scene
}

type ExperienceMode = 'driver' | 'engineer'
type CameraMode = 'default' | 'driver' | 'engineer' | 'quiz' | 'end'
type AnimationState = 'idle' | 'animating' | 'complete'

// Helper to get camera settings for a mode
const getCameraSettings = (experience: ExperienceData, mode: CameraMode) => {
  const modeSettings =
    mode === 'default'
      ? experience.model.defaultCamera
      : mode === 'driver'
        ? experience.model.cameraDriverMode
        : mode === 'engineer'
          ? experience.model.cameraEngineerMode
          : mode === 'quiz'
            ? experience.model.cameraQuiz
            : experience.model.cameraEnd
  return {
    position: new Vector3(modeSettings.x ?? 0, modeSettings.y ?? 2, modeSettings.z ?? 5),
    target: new Vector3(
      modeSettings.targetX ?? 0,
      modeSettings.targetY ?? 0,
      modeSettings.targetZ ?? 0
    ),
    azimuthRotation: modeSettings.azimuthRotation,
    polarRotation: modeSettings.polarRotation,
    maxPolarAngle: modeSettings.maxPolarAngle,
    minPolarAngle: modeSettings.minPolarAngle,
    maxAzimuthAngle: modeSettings.maxAzimuthAngle,
    minAzimuthAngle: modeSettings.minAzimuthAngle,
  }
}

export default function Experience() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const [session, setSession] = useState<SessionData | null>(null)
  const [experience, setExperience] = useState<ExperienceData | null>(null)
  const [pointOfInterests, setPointOfInterests] = useState<PointOfInterest[]>([])
  const [visitedPOIs, setVisitedPOIs] = useState<Set<number>>(new Set())
  const [activePOI, setActivePOI] = useState<number | null>(null)
  const [poiCameraTargets, setPOICameraTargets] = useState<{
    position: Vector3
    lookAt: Vector3
    azimuthRotation?: number
    polarRotation?: number
  } | null>(null)
  const [stage, setStage] = useState<'' | 'home' | 'educate' | 'quiz'>('home')
  // Get mode from URL params, default to 'engineer'
  const experienceMode = (searchParams.get('mode') as ExperienceMode) || 'engineer'
  const [cameraMode, setCameraMode] = useState<CameraMode>(
    (searchParams.get('mode') as CameraMode) || 'default'
  )

  const [progressBar, setProgressBar] = useState(0)
  const [modelUrl, setModelUrl] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [, setCurrentCameraInfo] = useState({
    position: { x: 0, y: 0, z: 0 },
    target: { x: 0, y: 0, z: 0 },
    distance: 0,
    zoom: 1,
    azimuth: 0,
    polar: 0,
  })
  // Get camera settings from experience data
  const [cameraPosition, setCameraPosition] = useState<[number, number, number]>([0, 0, 5])
  const [cameraFov, setCameraFov] = useState(50)
  const [cameraNear, setCameraNear] = useState(0.1)
  const [cameraFar, setCameraFar] = useState(1000)

  const [animationState, setAnimationState] = useState<AnimationState>('idle')
  const [hasClickedOnce, setHasClickedOnce] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const controlsRef = useRef<any>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cameraRef = useRef<any>(null)

  const [showBackHomeModal, setShowBackHomeModal] = useState(false)

  // Inactivity modal state
  const [showInactivityModal, setShowInactivityModal] = useState(false)
  const [inactivityCountdown, setInactivityCountdown] = useState(60)

  const { progress } = useProgress()

  useEffect(() => {
    if (progress >= 0) {
      setProgressBar(55 + progress * 0.45)
    }
  }, [progress])

  // Ensure SFX are registered globally so any screen can play them instantly.
  useEffect(() => {
    const se = experience?.soundEffects
    if (!se) return
    const toUrl = (m?: Media | null) =>
      m?.url?.startsWith('data:') ? m.url : m?.['filename'] ? getAssetUrl(m.filename) : null

    const entries: Record<SoundKey, SrcConfig | null> = {
      mainButton: toUrl(se.mainButton),
      tabs: toUrl(se.tabs),
      closeButton: toUrl(se.closeButton),
      pointOfInterest: toUrl(se.pointOfInterest),
    }

    // Filter out nulls and register
    const reg: Record<SoundKey, SrcConfig> = {}
    Object.entries(entries).forEach(([k, v]) => {
      if (v) reg[k] = v
    })
    if (Object.keys(reg).length) audio.register(reg)
  }, [experience?.soundEffects])

  // Background music: register + loop at 50% volume. Attempt to play immediately,
  // and also on first user interaction if autoplay is blocked.
  useEffect(() => {
    const sfx = experience?.soundEffects?.backgroundMusic
    if (!sfx) return
    const url = sfx.url.startsWith('data:') ? sfx.url : getAssetUrl(sfx.filename)
    if (!url) return

    audio.register({ bgm: { src: url, loop: true, volume: 0.1 } })
    audio.play('bgm', { volume: 0.25 })

    const kickstart = () => {
      if (!audio.isPlaying('bgm')) audio.play('bgm', { volume: 0.1 })
      window.removeEventListener('pointerdown', kickstart)
      window.removeEventListener('keydown', kickstart)
      window.removeEventListener('touchstart', kickstart)
    }
    window.addEventListener('pointerdown', kickstart, { once: true, passive: true })
    window.addEventListener('keydown', kickstart, { once: true })
    window.addEventListener('touchstart', kickstart, { once: true, passive: true })

    return () => {
      window.removeEventListener('pointerdown', kickstart)
      window.removeEventListener('keydown', kickstart)
      window.removeEventListener('touchstart', kickstart)
      audio.stop('bgm')
      audio.unload('bgm')
    }
  }, [experience?.soundEffects?.backgroundMusic])

  const showInactivityModalRef = useRef(showInactivityModal)
  useEffect(() => {
    showInactivityModalRef.current = showInactivityModal
  }, [showInactivityModal])

  useEffect(() => {
    const loadExperience = async () => {
      if (!id) return

      try {
        setProgressBar(10)
        const data = await getExperience(id)
        setProgressBar(15)

        // Cache the media (images/audio only, not model)
        const cachedExperience = await cacheExperienceMedia(data, cacheProgress => {
          // Map cache progress (0-100) to our progress bar range (15-45)
          const mappedProgress = 15 + cacheProgress * 0.3
          setProgressBar(mappedProgress)
        })

        // Set the experience with cached media URLs
        setExperience(cachedExperience)
        setProgressBar(45)

        // Load point of interests
        if (cachedExperience.model?.id) {
          const poiData = await getPointOfInterestsByModelAndModelId(
            cachedExperience.model.id,
            experienceMode
          )

          // Cache POI media with progress tracking (45-55%)
          if (poiData.docs && poiData.docs.length > 0) {
            const cachedPOIs = await cachePointOfInterests(poiData.docs, poiCacheProgress => {
              const mappedProgress = 45 + poiCacheProgress * 0.1
              setProgressBar(mappedProgress)
            })
            setPointOfInterests(cachedPOIs)
            setProgressBar(55)
          } else {
            setPointOfInterests([])
            setProgressBar(55)
          }
        }

        const modelName = cachedExperience.model?.source?.filename
        if (modelName) {
          const fullUrl = getAssetUrl(modelName)
          setModelUrl(fullUrl)
        } else {
          setError('Failed to load experience')
          return
        }

        if (cachedExperience.model && cachedExperience.model.defaultCamera) {
          setCameraFov(cachedExperience.model.defaultCamera.fov ?? 50)
          setCameraNear(cachedExperience.model.defaultCamera.near ?? 0.1)
          setCameraFar(cachedExperience.model.defaultCamera.far ?? 1000)
          setCameraPosition([
            cachedExperience.model.defaultCamera.x ?? 0,
            cachedExperience.model.defaultCamera.y ?? 0,
            cachedExperience.model.defaultCamera.z ?? 5,
          ])
        }
      } catch (error) {
        console.error('Error loading experience:', error)
        setError('Failed to load experience')
      }
    }

    loadExperience()
  }, [id, experienceMode])

  const handlePOIClick = (poiId: number) => {
    if (animationState === 'animating') return
    // Play POI click SFX
    audio.play('pointOfInterest')
    if (activePOI && activePOI !== poiId) {
      // Mark previous active POI as visited
      setVisitedPOIs(prev => new Set([...prev, activePOI]))
    }
    setActivePOI(poiId)

    // Find the clicked POI and move camera to its position
    const clickedPOI = pointOfInterests.find(poi => poi.id === poiId)
    if (clickedPOI && controlsRef.current) {
      // Animate camera to POI camera position
      const targetPosition = new Vector3(
        clickedPOI.cameraPosition.x,
        clickedPOI.cameraPosition.y,
        clickedPOI.cameraPosition.z
      )
      const targetLookAt = new Vector3(
        clickedPOI.cameraPosition.targetX,
        clickedPOI.cameraPosition.targetY,
        clickedPOI.cameraPosition.targetZ
      )

      const modeSettings =
        cameraMode === 'driver'
          ? experience?.model.cameraDriverMode
          : experience?.model.cameraEngineerMode

      // Start POI camera animation
      setAnimationState('animating')
      setHasClickedOnce(true)

      // Store POI camera targets for animation
      setPOICameraTargets({
        position: targetPosition,
        lookAt: targetLookAt,
        azimuthRotation:
          clickedPOI.cameraPosition.azimuthRotation ?? modeSettings?.azimuthRotation ?? undefined,
        polarRotation:
          clickedPOI.cameraPosition.polarRotation ?? modeSettings?.polarRotation ?? undefined,
      })

      if (experience?.id) {
        viewPointOfInterest(poiId, experience.id)
      }
    }
  }

  const resetExperience = async () => {
    setActivePOI(null)
    setVisitedPOIs(new Set())
    setPOICameraTargets(null)
    setProgressBar(100)
    setShowInactivityModal(false)
    setInactivityCountdown(60)

    setError(null)
    setStage('home')
    setCameraMode('default')
    setCameraPosition([
      experience?.model?.defaultCamera?.x ?? 0,
      experience?.model?.defaultCamera?.y ?? 0,
      experience?.model?.defaultCamera?.z ?? 5,
    ])

    setAnimationState('animating')
    setHasClickedOnce(false)
    if (session && session.id) {
      endSession(session.id)
      setSession(null)
    }
  }

  // Inactivity timer for educate & quiz stage
  useEffect(() => {
    if (stage !== 'educate' && stage !== 'quiz') return

    let inactivityTimeout: number | null = null
    let countdownInterval: number | null = null

    const showInactivityWarning = () => {
      setShowInactivityModal(true)
      setInactivityCountdown(60)

      // Start countdown timer
      countdownInterval = setInterval(() => {
        setInactivityCountdown(prev => {
          if (prev <= 1) {
            // Auto-reset after 1 minute of showing modal
            resetExperience()
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }

    // Helper to clear and set inactivity timer
    const resetInactivityTimer = () => {
      if (showInactivityModalRef.current) return

      if (inactivityTimeout) clearTimeout(inactivityTimeout)
      if (countdownInterval) clearInterval(countdownInterval)

      // Reset modal state
      setShowInactivityModal(false)
      setInactivityCountdown(60)

      if (!showInactivityModal) {
        // First timer: 2 minutes to show warning
        inactivityTimeout = setTimeout(
          () => {
            showInactivityWarning()
          },
          2 * 60 * 1000
          // 5 * 1000
        )
      } else {
        // After warning shown, reset to 1 minute countdown
        inactivityTimeout = setTimeout(
          () => {
            resetExperience()
          },
          1 * 60 * 1000
          // 5 * 1000
        )
      }
    }

    // List of events that count as user activity
    const activityEvents = [
      'pointerdown',
      'pointermove',
      'mousedown',
      'mousemove',
      'touchstart',
      'touchmove',
      'wheel',
      'keydown',
    ]

    // Handler to reset inactivity timer
    const handleActivity = () => {
      if (showInactivityModalRef.current) return
      resetInactivityTimer()
    }

    // Listen for activity events on window
    activityEvents.forEach(event =>
      window.addEventListener(event, handleActivity, { passive: true })
    )

    // Start the timer initially
    resetInactivityTimer()

    return () => {
      if (inactivityTimeout) clearTimeout(inactivityTimeout)
      if (countdownInterval) clearInterval(countdownInterval)
      activityEvents.forEach(event => window.removeEventListener(event, handleActivity))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, experience, session])

  const returnToDefaultView = () => {
    if (animationState === 'animating') return
    if (stage === 'educate' && activePOI && !poiCameraTargets) {
      setActivePOI(null)
      setVisitedPOIs(prev => new Set([...prev, activePOI]))
      // Return to default mode view
      setPOICameraTargets(null)
      setAnimationState('animating')
    }
  }

  const handleEducate = () => {
    if (!experience) return

    setStage('educate')
    setCameraMode(experienceMode === 'driver' ? 'driver' : 'engineer')
    setPOICameraTargets({
      position: getCameraSettings(experience, cameraMode).position,
      lookAt: getCameraSettings(experience, cameraMode).target,
      azimuthRotation: getCameraSettings(experience, cameraMode).azimuthRotation,
      polarRotation: getCameraSettings(experience, cameraMode).polarRotation,
    })
    setActivePOI(null)
    setPOICameraTargets(null)
    setAnimationState('animating')
  }

  const startSession = async () => {
    if (experience) {
      setHasClickedOnce(true)
      try {
        const session = await createSession(experience?.id, experienceMode)

        // Replace session question media URLs with cached ones
        if (session.doc.questions && experience.questions?.docs) {
          const updatedQuestions = session.doc.questions.map((sessionQuestion: QuestionData) => {
            // Find matching question in cached experience questions
            const cachedQuestion = experience.questions?.docs.find(
              (expQuestion: QuestionData) => expQuestion.id === sessionQuestion.id
            )

            if (cachedQuestion) {
              return {
                ...sessionQuestion,
                audios: {
                  preQuestion:
                    cachedQuestion.audios?.preQuestion || sessionQuestion.audios?.preQuestion,
                  correctAnswer:
                    cachedQuestion.audios?.correctAnswer || sessionQuestion.audios?.correctAnswer,
                  incorrectAnswer:
                    cachedQuestion.audios?.incorrectAnswer ||
                    sessionQuestion.audios?.incorrectAnswer,
                },
              }
            }
            return sessionQuestion
          })

          setSession({
            ...session.doc,
            questions: updatedQuestions,
          })
        } else {
          setSession(session.doc)
        }
        console.log('Session started:', session)
      } catch (error) {
        console.error('Error starting session:', error)

        // Select 5 random questions of the same mode from experience to set the session, if there are less than 5 available, select what's available
        if (experience && experience.questions && Array.isArray(experience.questions.docs)) {
          const filteredQuestions = experience.questions.docs.filter(
            (q: QuestionData) => q.mode === experienceMode
          )
          // Shuffle the filtered questions
          const shuffled = filteredQuestions
            .map((q: QuestionData) => ({ q, sort: Math.random() }))
            .sort((a: { sort: number }, b: { sort: number }) => a.sort - b.sort)
            .map((a: { q: QuestionData }) => a.q)
          // Select up to 5
          const selectedQuestions = shuffled.slice(0, 5)
          setSession({
            start: new Date().toISOString(),
            end: null,
            mode: experienceMode,
            questions: selectedQuestions,
          })
        }
      }

      setCameraMode('quiz')
      setPOICameraTargets(
        experience?.model?.cameraQuiz
          ? {
              position: getCameraSettings(experience, 'quiz').position,
              lookAt: getCameraSettings(experience, 'quiz').target,
              azimuthRotation: getCameraSettings(experience, 'quiz').azimuthRotation,
              polarRotation: getCameraSettings(experience, 'quiz').polarRotation,
            }
          : {
              position: new Vector3(-8.36, 12.84, 0.43),
              lookAt: new Vector3(-8.45, -0.26, 2.04),
              azimuthRotation: 3.084,
              polarRotation: 0.123,
            }
      )
      setAnimationState('animating')
      setStage('quiz')
    } else {
      setError('Experience not found')
    }
  }

  const endingSession = async () => {
    if (session && session.id) {
      try {
        await endSession(session.id)
        setSession(null)
      } catch (error) {
        console.error('Error ending session:', error)
      }
    }
  }
  const handleBackToEducate = async () => {
    handleEducate()
    await endingSession()
  }

  const handleQuizFinished = () => {
    if (!experience) return
    setCameraMode('end')
    setPOICameraTargets({
      position: getCameraSettings(experience, 'end').position,
      lookAt: getCameraSettings(experience, 'end').target,
      azimuthRotation: getCameraSettings(experience, 'end').azimuthRotation,
      polarRotation: getCameraSettings(experience, 'end').polarRotation,
    })
    setAnimationState('animating')
  }

  if (error) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-black text-xl text-white">
        {error}
      </div>
    )
  }

  if (!experience || (experience && !modelUrl)) {
    return <LoadingScreen progress={progressBar} />
  }

  const bgColor =
    stage === 'quiz' ? experience.colours?.quizBackground : experience.colours?.exploreBackground
  const bgDotsColor =
    stage === 'quiz'
      ? experience.colours?.quizDotsOnBackground
      : experience.colours?.exploreDotsOnBackground

  return (
    <div className="relative h-screen w-screen overflow-hidden">
      <Suspense fallback={<LoadingScreen progress={progressBar} />}>
        {/* 2D Background */}
        <DottedBg
          bg={bgColor}
          dots={bgDotsColor}
          dotSize="48px 48px"
          topInset="10px"
          leftInset="6px"
          className="absolute top-0 left-0 z-0 h-full w-full"
        />
        {/* 3D Experience */}
        <Canvas
          camera={{
            position: [0, 0, 5],
            fov: 50,
            near: 0.1,
            far: 1000,
          }}
          className={`!absolute top-0 left-0 z-10 h-full w-full ${stage === 'quiz' && session ? '!pointer-events-none' : ''}`}
          gl={{ alpha: true, antialias: true }}
        >
          <Stage adjustCamera={false}>
            <Model url={modelUrl} spin={stage === 'home' && animationState === 'idle'} />
          </Stage>

          {/* Point of Interest Lights - only show after animation completes */}
          {stage === 'educate' &&
            pointOfInterests.map(poi => (
              <PointOfInterestLight
                key={poi.id}
                poi={poi}
                isActive={activePOI === poi.id}
                isVisited={visitedPOIs.has(poi.id)}
                primaryColour={experience.colours?.poiPrimary || '#BE95FF'}
                secondaryColour={experience.colours?.poiSecondary || '#E8DAFF'}
                mode={experienceMode}
                onClick={() => handlePOIClick(poi.id)}
              />
            ))}

          <ambientLight intensity={0.5} />
          <directionalLight position={[5, 5, 5]} />
          <PerspectiveCamera
            ref={cameraRef}
            makeDefault
            fov={cameraFov}
            near={cameraNear}
            far={cameraFar}
            position={cameraPosition}
          />
          <CameraControls
            ref={controlsRef}
            camera={cameraRef.current}
            dollySpeed={0.5}
            truckSpeed={2}
            maxPolarAngle={
              experience
                ? (getCameraSettings(experience, cameraMode).maxPolarAngle ?? Math.PI / 2)
                : Math.PI / 2
            }
            minDistance={controlsRef.current ? controlsRef.current.distance : Number.EPSILON}
            maxDistance={controlsRef.current ? controlsRef.current.distance : Infinity}
            enabled={animationState !== 'animating'}
          />

          {/* Camera Animator */}
          {experience && (
            <CameraAnimator
              targetPosition={
                poiCameraTargets
                  ? poiCameraTargets.position
                  : getCameraSettings(experience, cameraMode).position
              }
              targetLookAt={
                poiCameraTargets
                  ? poiCameraTargets.lookAt
                  : getCameraSettings(experience, cameraMode).target
              }
              targetRotation={
                poiCameraTargets
                  ? {
                      azimuthRotation: poiCameraTargets.azimuthRotation,
                      polarRotation: poiCameraTargets.polarRotation,
                    }
                  : {
                      azimuthRotation: getCameraSettings(experience, cameraMode).azimuthRotation,
                      polarRotation: getCameraSettings(experience, cameraMode).polarRotation,
                    }
              }
              isAnimating={animationState === 'animating'}
              onAnimationComplete={() => {
                setAnimationState('complete')
                if (controlsRef.current && stage !== 'educate' && stage !== 'quiz') {
                  const cameraSettings = getCameraSettings(experience, cameraMode)
                  // Configure CameraControls constraints
                  if (cameraSettings.maxPolarAngle !== undefined) {
                    controlsRef.current.maxPolarAngle = cameraSettings.maxPolarAngle
                  }
                  if (cameraSettings.minPolarAngle !== undefined) {
                    controlsRef.current.minPolarAngle = cameraSettings.minPolarAngle
                  }
                  if (cameraSettings.maxAzimuthAngle !== undefined) {
                    controlsRef.current.maxAzimuthAngle = cameraSettings.maxAzimuthAngle
                  }
                  if (cameraSettings.minAzimuthAngle !== undefined) {
                    controlsRef.current.minAzimuthAngle = cameraSettings.minAzimuthAngle
                  }
                }
                // Clear POI camera targets after animation
                if (poiCameraTargets) {
                  setPOICameraTargets(null)
                }
                if (stage === 'home') {
                  setAnimationState('idle')
                }
              }}
              controlsRef={controlsRef}
            />
          )}

          {/* Camera debug component */}
          <CameraDebug
            controlsRef={controlsRef}
            onCameraUpdate={info => {
              setCurrentCameraInfo(info)
            }}
          />
        </Canvas>
        {!hasClickedOnce && animationState === 'idle' && stage === 'home' && (
          <div className="absolute top-0 left-0 z-20 h-full w-full">
            <HomePanel mode={experienceMode} experience={experience} onStart={handleEducate} />
          </div>
        )}

        {stage === 'educate' && (
          <EducatePanel
            hasClickedOnce={hasClickedOnce}
            isAnimating={animationState === 'animating'}
            isInActivePOI={!activePOI}
            mode={experienceMode}
            experience={experience}
            onLogoClick={() => setShowBackHomeModal(true)}
            onStart={startSession}
          />
        )}

        {stage === 'quiz' && session && (
          <QuizPanel
            mode={experienceMode}
            cameraSettings={getCameraSettings(experience, 'quiz')}
            session={session}
            experience={experience}
            isAnimating={animationState === 'animating'}
            controlsRef={controlsRef}
            onLogoClick={() => setShowBackHomeModal(true)}
            onBackToEducate={handleBackToEducate}
            onBackToHome={resetExperience}
            onQuizFinished={handleQuizFinished}
          />
        )}

        {/* POI Navigation Info */}
        {stage === 'educate' && activePOI && (
          <RightDrawer
            pointOfInterests={pointOfInterests}
            activePOI={activePOI}
            isAnimating={animationState === 'animating'}
            experience={experience}
            onClose={() => {
              returnToDefaultView()
            }}
          />
        )}
      </Suspense>

      {/* Inactivity Modal */}
      <PopupModal
        isVisible={showInactivityModal}
        text={'You’ve been idle for a while.\nDo you want to resume the session?'}
        countdown={inactivityCountdown}
        yesCtaLabel="Resume"
        noCtaLabel="End Session"
        onYes={() => {
          setShowInactivityModal(false)
          setInactivityCountdown(60)
        }}
        onNo={async () => {
          setShowInactivityModal(false)
          setInactivityCountdown(0) // Force auto-reset
          await resetExperience()
        }}
        onClose={() => {
          setShowInactivityModal(false)
          setInactivityCountdown(60)
        }}
      />

      {/* Back Home Modal */}
      <PopupModal
        isVisible={showBackHomeModal}
        text={'You are about to return to home screen.\n Do you want to resume the session?'}
        yesCtaLabel="Resume"
        noCtaLabel="End Session"
        onYes={() => setShowBackHomeModal(false)}
        onNo={async () => {
          await resetExperience()
          setShowBackHomeModal(false)
        }}
        onClose={() => setShowBackHomeModal(false)}
      />
    </div>
  )
}
